import { ChartDataset, ScatterDataPoint } from 'chart.js';
import stringify from 'json-stringify-pretty-compact';
import { ChargePointLog } from '../../../../../../../app/ApiGen';
import { evnexPalette, EvnexPalette } from '../../../../../../../design-system';
import { ChargePointLogs } from '../../../../../../../utils/ChargePointLogs';
import { MeterValue, MeterValueLogData } from '../../../../../../../utils/meterValues';
import { Normalised } from '../../../../../../../utils/request';

export type EventChartDatum = ScatterDataPoint & {
  event: { data: Normalised<ChargePointLog>['eventPayload']; type: Normalised<ChargePointLog>['eventType'] };
};

export const ChargePointLogEventTypes: Normalised<ChargePointLog>['eventType'][] = [
  'Authorize',
  'BootNotification',
  'ChangeAvailability',
  'ChangeConfiguration',
  'ClearCache',
  'ClearChargingProfile',
  'DataTransfer',
  'DiagnosticsStatusNotification',
  'FirmwareStatusNotification',
  'GetCompositeSchedule',
  'GetConfiguration',
  'GetDiagnostics',
  'Heartbeat',
  'MeterValues',
  'RemoteStartTransaction',
  'RemoteStopTransaction',
  'Reset',
  'SetChargingProfile',
  'StartTransaction',
  'StatusNotification',
  'StopTransaction',
  'UnlockConnector',
  'UpdateFirmware',
  'WebSocketClose',
  'WebSocketConnection',
];

function isMeterValueData(data: Normalised<ChargePointLog>['eventPayload']): data is MeterValueLogData {
  return data !== undefined && 'meterValue' in data;
}

export class EventChartModel {
  private readonly logs: ChargePointLogs;

  private readonly palette: EvnexPalette = evnexPalette;

  private readonly styles = {
    backgroundColor: this.palette.tussock.main,
    borderColor: this.palette.tussock.contrastText,
    pointHoverRadius: 20,
  };

  constructor(args: { logs: ChargePointLogs; palette?: EvnexPalette }) {
    const { logs, palette } = args;
    this.logs = logs;
    if (palette) {
      this.palette = palette;
    }
  }

  private static extractSampledValues(param: { value: MeterValue }): string[] {
    const { value } = param;
    const stringifiedSampledValues = stringify(value.sampledValue, { indent: 2, maxLength: 150 });
    // Remove opening and closing brackets
    return stringifiedSampledValues.replace('\n]', '').split(/\n/).slice(1);
  }

  private static getMeterValueTooltip(data: MeterValueLogData): string[] {
    const { connectorId, meterValue, transactionId } = data;
    const values = meterValue.reduce<string[]>((acc, value, index) => {
      const readableSampledValues = this.extractSampledValues({ value });

      const isFirstItem = acc.length === 0;
      const isLastItem = index + 1 === meterValue.length;
      const descriptors = `{"timestamp": "${value.timestamp}", "sampledValue": [`;

      return [
        ...acc,
        isFirstItem ? `[${descriptors}` : descriptors,
        ...readableSampledValues,
        isLastItem ? ']}]' : ']},',
      ];
    }, []);

    const connectorIdDescriptor = `Connector ID: ${connectorId}`;
    const descriptors = transactionId
      ? `${connectorIdDescriptor}, Transaction ID: ${transactionId}`
      : connectorIdDescriptor;

    return [`MeterValues - ${descriptors}`, ...values];
  }

  private static getTooltipLines(args: { data: Normalised<ChargePointLog>['eventPayload'] }): string[] {
    const { data } = args;

    const stringified = stringify(data, { indent: 2 });

    const splitByNewline = stringified.split(/\n/);

    const tooManyLines = splitByNewline.length > 30;
    if (tooManyLines) {
      const condensed = stringify(data, { maxLength: 500 });
      return condensed.split(/\n/);
    }

    return splitByNewline;
  }

  private static toChartDatum(args: { event: Normalised<ChargePointLog>; eventIndex: number }): EventChartDatum {
    const { event, eventIndex } = args;
    const isEventResponse = event.eventType.endsWith('Resp');
    return {
      x: new Date(event.serverEventDate).getTime(),
      y: isEventResponse ? eventIndex : eventIndex + 0.5,
      event: { data: event.eventPayload, type: event.eventType },
    };
  }

  static getTooltip(datum: EventChartDatum): string[] {
    const { data, type } = datum.event;

    if (!data) {
      return [`${type}: ''`];
    }

    const keys = Object.keys(data);
    if (keys.length === 0) {
      return [`${type}: {}`];
    }

    if (isMeterValueData(data)) {
      return this.getMeterValueTooltip(data);
    }

    return [`${type}:`, ...this.getTooltipLines({ data })];
  }

  toChartData(): ChartDataset<'line'>[] {
    const orderedEvents = this.logs.sortByServerDate();

    return ChargePointLogEventTypes.reduce<ChartDataset<'line'>[]>((acc, eventType, eventIndex) => {
      const relevantEvents = orderedEvents.ofType(eventType);

      if (relevantEvents.length === 0) {
        return acc;
      }

      const data = relevantEvents.toChartData<EventChartDatum>({
        toChartDatum: (event) => EventChartModel.toChartDatum({ event, eventIndex }),
      });
      return [...acc, { label: eventType, data, ...this.styles }];
    }, []);
  }
}
